import { Button, Input, Stack } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { notify } from "@/ui/chakra/notify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { useGetZone } from "@/core/zones/useCases/useGetZone";
import { useGetCompanies } from "@/core/companies/useCases/useGetCompanies";
import { applicantSchema } from "../schemas/applicantSchema";

import { useUpdateApplicant } from "../useCases/useUpdateApplicant";
import { Applicant } from "@/shared/core-vouchers";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field, NativeSelectField, NativeSelectRoot } from "@/ui/chakra";

type ApplicantFormInput = z.infer<typeof applicantSchema>;

export interface UpdateApplicantModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicant: Applicant;
}

export const UpdateApplicantModal = ({
  isOpen,
  onClose,
  applicant,
}: UpdateApplicantModalProps) => {
  const { data: zones = [] } = useGetZone();
  const { data: companies = [] } = useGetCompanies();
  const { isPending, mutate: updateApplicant } = useUpdateApplicant();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ApplicantFormInput>({
    resolver: zodResolver(applicantSchema),
    defaultValues: {
      name: applicant.name,
      email: applicant.email,
      phone: applicant.phone,
      companyId: applicant.company.id,
      zoneId: applicant.zone.id,
      enabled: true,
    },
  });

  const onFormSubmit = (data: ApplicantFormInput) => {
    updateApplicant(
      { id: applicant.id, updatedApplicant: data },
      {
        onSuccess: () => {
          notify.success("Solicitante actualizado");
          onClose();
        },
        onError: (error) =>
          notify.error("No se pudo actualizar el solicitante", error),
      },
    );
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Editar solicitante</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Stack gap={4}>
              <Field
                label="Nombre"
                required
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  {...register("name")}
                  placeholder="Nombre"
                  autoComplete="off"
                />
              </Field>
              <Field
                label="Correo"
                invalid={!!errors.email}
                errorText={errors.email?.message}
              >
                <Input
                  {...register("email")}
                  placeholder="correo@belray.cl"
                  autoComplete="off"
                />
              </Field>
              <Field
                label="Teléfono"
                invalid={!!errors.phone}
                errorText={errors.phone?.message}
              >
                <Input
                  {...register("phone")}
                  placeholder="Teléfono"
                  autoComplete="off"
                />
              </Field>
              <Field
                label="Empresa"
                required
                invalid={!!errors.companyId}
                errorText={errors.companyId?.message}
              >
                <NativeSelectRoot>
                  <NativeSelectField
                    {...register("companyId")}
                    placeholder="Seleccione una empresa"
                  >
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              <Field
                label="Zona"
                required
                invalid={!!errors.zoneId}
                errorText={errors.zoneId?.message}
              >
                <NativeSelectRoot>
                  <NativeSelectField
                    {...register("zoneId")}
                    placeholder="Seleccione una zona"
                  >
                    {zones.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            </Stack>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isPending}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
